import * as React from 'react'
import * as z from 'zod'

import {
  type CustomCell,
  type CustomRenderer,
  drawTextCell,
  GridCellKind,
  type ProvideEditorCallback,
  TextCellEntry,
} from '@glideapps/glide-data-grid'
import { HTMLInputToIsoString, isoStringToHTMLInput } from '~/lib/date-utils'
import { Input } from '~/sotano-kit/primitives/input'

export const datePickerCellSchema = z.object({
  kind: z.literal('date-picker-cell'),
  date: z.string().nullable(),
  displayDate: z.string(),
  format: z.union([z.literal('date'), z.literal('datetime-local')]),
  timezoneOffset: z.number().optional(),
  min: z.union([z.date(), z.string()]).optional(),
  max: z.union([z.date(), z.string()]).optional(),
  step: z.string().optional(),
})

export type DatePickerCellProps = z.infer<typeof datePickerCellSchema>
export type DateKind = 'date' | 'datetime-local'

export const formatValueForHTMLInput = (
  dateKind: DateKind,
  dateAsString: string | undefined | null
): string => {
  if (dateAsString === undefined || dateAsString === null) return ''

  const date = new Date(dateAsString)
  if (isNaN(date.getTime())) return ''

  const isoDate = date.toISOString()
  switch (dateKind) {
    case 'date':
      return isoDate.split('T')[0] ?? ''
    case 'datetime-local':
      return isoDate.replace('Z', '')
    default:
      throw new Error(`Unknown date kind ${dateKind}`)
  }
}

export type DatePickerCell = CustomCell<DatePickerCellProps>

const Editor: ReturnType<ProvideEditorCallback<DatePickerCell>> = (cell) => {
  const cellData = cell.value.data
  const { format, displayDate } = cellData
  const step =
    cellData.step !== undefined && !Number.isNaN(Number(cellData.step))
      ? Number(cellData.step)
      : undefined

  const minValue =
    cellData.min instanceof Date
      ? formatValueForHTMLInput(format, cellData.min.getTime().toString())
      : cellData.min

  const maxValue =
    cellData.max instanceof Date
      ? formatValueForHTMLInput(format, cellData.max.getTime().toString())
      : cellData.max

  const date = cellData.date

  if (cell.value.readonly) {
    return (
      <TextCellEntry
        highlight={true}
        autoFocus={false}
        disabled={true}
        value={displayDate ?? ''}
        onChange={() => undefined}
      />
    )
  }

  return (
    <Input
      className="min-h-[26px] border-none text-xs text-dark-muted outline-none bg-transparent tracking-tighter"
      required
      type={format}
      defaultValue={isoStringToHTMLInput(date ?? '', {
        useTime: format === 'datetime-local',
      })}
      min={minValue}
      max={maxValue}
      step={step}
      autoFocus={true}
      onChange={(event) => {
        if (isNaN(event.target.valueAsNumber)) {
          // The user has cleared the date, contribute as undefined
          cell.onChange({
            ...cell.value,
            data: { ...cell.value.data, date: null },
          })
        } else {
          cell.onChange({
            ...cell.value,
            data: {
              ...cell.value.data,
              date: HTMLInputToIsoString(event.target.value),
            },
          })
        }
      }}
    />
  )
}

export const DatePickerRenderer: CustomRenderer<DatePickerCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is DatePickerCell =>
    (cell.data as any).kind === 'date-picker-cell',
  draw: (args, cell) => {
    const { displayDate } = cell.data
    drawTextCell(args, displayDate, cell.contentAlign)
    return true
  },
  needsHover: false,
  needsHoverPosition: false,
  measure: (ctx, cell, theme) => {
    const { displayDate } = cell.data
    return ctx.measureText(displayDate).width + theme.cellHorizontalPadding * 2
  },
  provideEditor: () => ({ editor: Editor, disablePadding: true }),
  onPaste: (v, d) => {
    let parseDateTimestamp = NaN

    if (v) {
      parseDateTimestamp = Number(v).valueOf()

      if (Number.isNaN(parseDateTimestamp)) {
        parseDateTimestamp = Date.parse(v)
      }
    }

    return {
      ...d,
      date: Number.isNaN(parseDateTimestamp)
        ? null
        : HTMLInputToIsoString(parseDateTimestamp),
    }
  },
}
